import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { ClientDto, PagedClientsDto } from '../../dtos/client.dto';
import { ClientMetricsDto } from '../../dtos/client-metrics.dto';
import {
  ClientConsultationAttachmentDto, ClientConsultationSectionDtoMap,
  PagedClientConsultationSummariesDto,
} from '../../dtos/clinical-history.dto';
import { ClientConsultationSectionKey } from '../../../domain/models/clinical-history.model';
import { PagedInvoicesDto } from '../../dtos/invoice.dto';
import { PagedEstimatesDto } from '../../dtos/estimate.dto';
import { ConsentDto } from '../../dtos/consent.dto';
import { PagedClientAppointmentsDto } from '../../dtos/client-appointment.dto';
import { PagedClientProfessionalsDto } from '../../dtos/client-professional.dto';
import { DepartmentTransferResultDto } from '@features/shared/departments/data/dtos/department-transfer-result.dto';

@Injectable()
export class ClientsRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public list(
    page: number,
    pageSize: number,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filters?: Record<string, string>,
    departmentId?: number,
  ): Observable<PagedClientsDto> {
    return this.http.get<PagedClientsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.list}`, {
      params: this.buildParams({ page, pageSize }, sortBy, sortDir, filters, departmentId),
    });
  }

  public search(
    term: string,
    page: number,
    pageSize: number,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filters?: Record<string, string>,
    departmentId?: number,
  ): Observable<PagedClientsDto> {
    return this.http.get<PagedClientsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.search}`, {
      params: this.buildParams({ term, page, pageSize }, sortBy, sortDir, filters, departmentId),
    });
  }

  public getById(id: string): Observable<ClientDto> {
    return this.http.get<ClientDto>(`${this.apiUrl}${API_ENDPOINTS.clients.byId(id)}`);
  }

  public getMetrics(departmentId?: number): Observable<ClientMetricsDto> {
    return this.http.get<ClientMetricsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.metrics}`, {
      params: departmentId === undefined ? {} : { departmentId },
    });
  }

  public create(payload: Record<string, unknown>, departmentId?: number): Observable<ClientDto> {
    return this.http.post<ClientDto>(`${this.apiUrl}${API_ENDPOINTS.clients.list}`, payload, {
      params: departmentId === undefined ? {} : { departmentId },
    });
  }

  public update(id: string, payload: Record<string, unknown>): Observable<ClientDto> {
    return this.http.patch<ClientDto>(`${this.apiUrl}${API_ENDPOINTS.clients.byId(id)}`, payload);
  }

  public delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${API_ENDPOINTS.clients.byId(id)}`);
  }

  public activate(id: string): Observable<ClientDto> {
    return this.http.post<ClientDto>(`${this.apiUrl}${API_ENDPOINTS.clients.activate(id)}`, {});
  }

  public moveToDepartment(id: string, departmentId: number): Observable<DepartmentTransferResultDto> {
    return this.http.post<DepartmentTransferResultDto>(
      `${this.apiUrl}${API_ENDPOINTS.clients.department(id)}`, { DepartmentId: departmentId },
    );
  }

  public claimDepartment(id: string, departmentId: number, email: string): Observable<DepartmentTransferResultDto> {
    return this.http.post<DepartmentTransferResultDto>(
      `${this.apiUrl}${API_ENDPOINTS.clients.departmentClaim(id)}`, { DepartmentId: departmentId, Email: email },
    );
  }

  // `GET /api/clients/{clientId}/consultations/summary` -- paginated `PagedDto<ConsultationSummaryDto>`
  // merged across every pet of the tutor, or narrowed to one of them when `patientId` is passed.
  // `page`/`pageSize` are real and forwarded to ProVet.
  public getConsultationSummaries(
    clientId: string, organizationId: number, page: number, pageSize: number, patientId?: string,
  ): Observable<PagedClientConsultationSummariesDto> {
    const params: Record<string, string | number> = { organizationId, page, pageSize };
    if (patientId !== undefined) params['patientId'] = patientId;

    return this.http.get<PagedClientConsultationSummariesDto>(
      `${this.apiUrl}${API_ENDPOINTS.clients.consultationsSummary(clientId)}`, { params },
    );
  }

  // One request per opened tab of the consultation detail page -- `ClientsRead`-gated mirror of
  // patients' `getConsultationSection`, same `section` literal resolving both the route and the
  // response type through `ClientConsultationSectionDtoMap`.
  public getConsultationSection<K extends ClientConsultationSectionKey>(
    clientId: string, patientId: string, consultationId: string, section: K,
  ): Observable<ClientConsultationSectionDtoMap[K]> {
    return this.http.get<ClientConsultationSectionDtoMap[K]>(
      `${this.apiUrl}${API_ENDPOINTS.clients.consultationSection(clientId, patientId, consultationId, section)}`,
    );
  }

  // `GET /api/billing/invoices` is a paginated `BillingController` endpoint (`PagedDto<InvoiceDto>`,
  // see BillingDtos.cs/PagedDto.cs) filtered by `clientId` -- `page`/`pageSize` are forwarded
  // as-is, the repository extracts `Items`/`TotalCount` from the envelope.
  public getInvoices(clientId: string, page: number, pageSize: number): Observable<PagedInvoicesDto> {
    return this.http.get<PagedInvoicesDto>(`${this.apiUrl}${API_ENDPOINTS.billing.invoices}`, {
      params: { clientId, page, pageSize },
    });
  }

  // `GET /api/billing/invoices/{invoiceId}/pdf` streams the PDF bytes directly (no JSON envelope),
  // hence `responseType: 'blob'` instead of the default JSON parsing every other method here uses.
  public getInvoicePdf(invoiceId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}${API_ENDPOINTS.billing.invoicePdf(invoiceId)}`, { responseType: 'blob' });
  }

  // Level 3 of the consultation-attachments feature, `ClientsRead`-gated mirror of patients'
  // `downloadConsultationAttachment` -- same rationale as `getConsultationSection` above.
  public downloadConsultationAttachment(
    clientId: string, patientId: string, consultationId: string, noteId: string, uploadId: string,
  ): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}${API_ENDPOINTS.clients.consultationAttachmentDownload(clientId, patientId, consultationId, noteId, uploadId)}`,
      { responseType: 'blob' },
    );
  }

  // `ClientsRead`-gated mirror of patients' `getConsultationAnalysisReferralAttachments` -- same
  // standalone-endpoint rationale as `downloadConsultationAttachment` above.
  public getConsultationAnalysisReferralAttachments(
    clientId: string, patientId: string, consultationId: string, referralId: string,
  ): Observable<readonly ClientConsultationAttachmentDto[]> {
    return this.http.get<readonly ClientConsultationAttachmentDto[]>(
      `${this.apiUrl}${API_ENDPOINTS.clients.consultationAnalysisReferralAttachments(clientId, patientId, consultationId, referralId)}`,
    );
  }

  // `GET /api/billing/estimates` mirrors `getInvoices` above -- a paginated `BillingController`
  // endpoint (`PagedDto<EstimateDto>`, see BillingDtos.cs/PagedDto.cs) filtered by `clientId`.
  public getEstimates(clientId: string, page: number, pageSize: number): Observable<PagedEstimatesDto> {
    return this.http.get<PagedEstimatesDto>(`${this.apiUrl}${API_ENDPOINTS.billing.estimates}`, {
      params: { clientId, page, pageSize },
    });
  }

  // `GET /api/organizations/{organizationId}/clients/{clientId}/consents` (ConsentsController.cs) returns both
  // pending and signed consents as a bare JSON array -- no `PagedDto` envelope, and the staff's
  // `organizationId` is resolved by the caller (`ClientDetailStore`, via `AuthStore.user()`) rather than
  // read here -- this `data/` layer must not import from `presentation/`
  // (`@features/shared/auth/presentation/store/auth.store`), which is where `AuthStore` lives; see
  // `tools/check-architecture.mjs`'s "data layer imports presentation" rule.
  public getConsents(organizationId: number, clientId: string): Observable<readonly ConsentDto[]> {
    return this.http.get<readonly ConsentDto[]>(
      `${this.apiUrl}${API_ENDPOINTS.consents.list(organizationId, clientId)}`,
    );
  }

  // `GET /api/appointments?clientId=...` is the staff-facing `AppointmentsController` list endpoint
  // (paged `Items`/`TotalCount` envelope), reached from this feature's own datasource -- same
  // cross-controller convention `getInvoices`/`getEstimates` above already follow for
  // `BillingController`, and the reason no cross-feature import of `@features/web/appointments` is
  // needed for the signature drawer's appointment picker. `startFrom` bounds the list to the recent
  // past + everything ahead, which is all a "link this consent to a cita" dropdown can usefully offer.
  public listAppointments(
    clientId: string, startFrom: string, pageSize: number,
  ): Observable<PagedClientAppointmentsDto> {
    return this.http.get<PagedClientAppointmentsDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.list}`, {
      params: { clientId, startFrom, page: 1, pageSize },
    });
  }

  // `GET /api/appointments/professionals` backs the signature drawer's optional veterinarian picker; same
  // cross-controller convention as `listAppointments` above, gated by `AppointmentsRead`.
  public listProfessionals(): Observable<PagedClientProfessionalsDto> {
    return this.http.get<PagedClientProfessionalsDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.professionals}`);
  }

  // `POST /api/organizations/{organizationId}/clients/{clientId}/consents/request` creates a new pending
  // `ConsentRequest` (staff asking the client to sign a document type + version) and returns it as
  // a `ConsentDto` with `Status: "Pending"`. `Channel` is a JSON string on the wire (`"Email"`/`"App"`,
  // backed by a `[JsonConverter(typeof(JsonStringEnumConverter))]` C# enum -- see
  // `ConsentRequests.cs`'s `CreateConsentRequestRequest`), mapped here from this feature's own
  // lowercase-literal channel type. `Message` is only included when non-empty -- same "omit rather
  // than send an explicit null/empty" convention `ClientMapper.toCreatePayload` already uses for its
  // own optional fields -- and the backend's `Message` is nullable/optional there too.
  // `AppointmentExternalId` follows that same omit-when-empty convention: it is an optional column
  // on `ConsentRequest` (see `CreateConsentRequestRequest`/`ConsentService.CreateRequestAsync`), so a
  // consent stays unlinked to any cita when staff picks nothing in the drawer.
  public requestConsent(
    organizationId: number, clientId: string, documentType: string, version: string,
    channel: 'email' | 'app', message?: string, appointmentExternalId?: string,
  ): Observable<ConsentDto> {
    return this.http.post<ConsentDto>(
      `${this.apiUrl}${API_ENDPOINTS.consents.request(organizationId, clientId)}`,
      {
        DocumentType: documentType,
        Version: version,
        Channel: channel === 'app' ? 'App' : 'Email',
        ...(message ? { Message: message } : {}),
        ...(appointmentExternalId ? { AppointmentExternalId: appointmentExternalId } : {}),
      },
    );
  }

  // `POST /api/organizations/{organizationId}/clients/{clientId}/consents/requests/{consentRequestId}/decline`
  // marks a pending `ConsentRequest` as declined and returns the updated `ConsentDto` (200 OK, not
  // 204/void -- see `ConsentsController.DeclineRequest`'s `ProducesResponseType`).
  public declineConsentRequest(
    organizationId: number, clientId: string, consentRequestId: string,
  ): Observable<ConsentDto> {
    return this.http.post<ConsentDto>(
      `${this.apiUrl}${API_ENDPOINTS.consents.decline(organizationId, clientId, consentRequestId)}`,
      {},
    );
  }

  // `filters` is forwarded as `filters[key]=value` query params via Angular's `HttpParams`
  // object-key expansion (standard ASP.NET `[FromQuery] Dictionary<string, string>` model
  // binding on the backend) -- see ClientsController.GetClients/PatientsController.GetPatients.
  // `departmentId`, in contrast, is its OWN top-level query param (`[FromQuery] int?
  // departmentId`, separate from the `filters` dict) -- see `ClientListQuery.departmentId`'s
  // own doc-comment.
  private buildParams(
    base: Record<string, string | number>,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filters?: Record<string, string>,
    departmentId?: number,
  ): Record<string, string | number> {
    const withSort = sortBy ? { ...base, sortBy, sortDir: sortDir ?? 'asc' } : base;
    const withDepartment = departmentId === undefined ? withSort : { ...withSort, departmentId };
    if (!filters || Object.keys(filters).length === 0) return withDepartment;

    const filterParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(filters)) {
      filterParams[`filters[${key}]`] = value;
    }
    return { ...withDepartment, ...filterParams };
  }
}
